import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../context/AppContext';
import { Cog6ToothIcon, SwatchIcon, PhotoIcon, BeakerIcon, ShieldExclamationIcon, MapIcon, EnvelopeIcon, DocumentTextIcon, AdjustmentsHorizontalIcon, CreditCardIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import EmailSettings from './EmailSettings';
import EmailTemplates from './EmailTemplates';
import StripeSettings from './StripeSettings';
import FlexFieldsConfig from './FlexFieldsConfig';

const DEFAULT_PLOT_COLORS = {
  available: '#22c55e',    // Green - plots with no contacts / available
  reserved: '#f59e0b',     // Amber - reserved plots
  sold: '#3b82f6',         // Blue - sold plots (has contacts, not interred)
  interred: '#6b7280',     // Gray - occupied plots
  hasVeteran: '#7c3aed',   // Purple - veteran occupied
  highlight: '#ef4444'     // Red - highlight color
};

const SiteSettings = () => {
  const { token, user, isAdmin, siteName, plotColors, headerLogo, loginLogo, showPlotSize, demoMode, googleTagId, googleTagEnvOverride, refreshSiteSettings } = useContext(AppContext);
  const navigate = useNavigate();

  // All hooks must be declared before any conditional returns
  const [activeSection, setActiveSection] = useState('general');
  const [formData, setFormData] = useState({ siteName: '', showPlotSize: false });
  const [colorData, setColorData] = useState(DEFAULT_PLOT_COLORS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadingHeader, setUploadingHeader] = useState(false);
  const [uploadingLogin, setUploadingLogin] = useState(false);
  const [resettingDemo, setResettingDemo] = useState(false);
  const [resetResults, setResetResults] = useState(null);
  const [analyticsData, setAnalyticsData] = useState({ googleTagId: '' });
  const headerFileRef = useRef(null);
  const loginFileRef = useRef(null);

  // Security check: Only admin users should access this page
  useEffect(() => {
    if (user && user.role !== 'admin') {
      // Redirect non-admin users to dashboard
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    setFormData({ siteName: siteName || '', showPlotSize: showPlotSize || false });
  }, [siteName, showPlotSize]);

  useEffect(() => {
    setColorData(plotColors || DEFAULT_PLOT_COLORS);
  }, [plotColors]);

  useEffect(() => {
    setAnalyticsData({ googleTagId: googleTagId || '' });
  }, [googleTagId]);

  // If user is not admin, show unauthorized message while redirecting
  if (!user || user.role !== 'admin') {
    return (
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <ShieldExclamationIcon className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-lg font-semibold text-gray-900">Access Denied</h3>
          <p className="mt-1 text-sm text-gray-500">
            You do not have permission to access this page. Admin access required.
          </p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch('/api/site-settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ siteName: formData.siteName, showPlotSize: formData.showPlotSize }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update site settings');
      }

      await refreshSiteSettings();
      setSuccess('Site settings updated successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleColorSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch('/api/site-settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ plotColors: colorData }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update color settings');
      }

      await refreshSiteSettings();
      setSuccess('Color settings updated successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleColorChange = (colorKey, value) => {
    setColorData(prev => ({ ...prev, [colorKey]: value }));
  };

  const resetColors = () => {
    setColorData(DEFAULT_PLOT_COLORS);
  };

  const handleAnalyticsSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch('/api/site-settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ googleTagId: analyticsData.googleTagId.trim() }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update analytics settings');
      }

      await refreshSiteSettings();
      setSuccess('Analytics settings updated successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (type, file) => {
    const setUploading = type === 'header' ? setUploadingHeader : setUploadingLogin;
    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append(type === 'header' ? 'headerLogo' : 'loginLogo', file);

      const response = await fetch(`/api/site-settings/logo/${type}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `Failed to upload ${type} logo`);
      }

      await refreshSiteSettings();
      setSuccess(`${type === 'header' ? 'Header' : 'Login'} logo updated successfully`);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleResetLogo = async (type) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`/api/site-settings/logo/${type}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `Failed to reset ${type} logo`);
      }

      await refreshSiteSettings();
      setSuccess(`${type === 'header' ? 'Header' : 'Login'} logo reset to default`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const isCustomLogo = (logoPath) => {
    return logoPath && logoPath.startsWith('/api/site-settings/uploads/');
  };

  const handleDemoReset = async () => {
    if (!window.confirm('Are you sure you want to reset demo data? This will delete all data created after the demo mode cutoff date. This action cannot be undone.')) {
      return;
    }

    setResettingDemo(true);
    setError('');
    setSuccess('');
    setResetResults(null);

    try {
      const response = await fetch('/api/demo-mode/reset', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to reset demo data');
      }

      setResetResults(data.deleted);
      setSuccess('Demo data has been reset successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setResettingDemo(false);
    }
  };

  const formatCutoffDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">You do not have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Site Settings</h1>
        <p className="mt-2 text-sm text-gray-700">
          Configure global settings for your site.
        </p>
      </div>

      <div className="lg:grid lg:grid-cols-12 lg:gap-x-8">
        <aside className="lg:col-span-3">
          <nav className="space-y-1">
            <button
              onClick={() => setActiveSection('general')}
              className={`group flex w-full items-center rounded-md px-3 py-2 text-sm font-medium ${
                activeSection === 'general'
                  ? 'bg-indigo-50 text-indigo-600'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Cog6ToothIcon
                className={`-ml-1 mr-3 h-6 w-6 flex-shrink-0 ${
                  activeSection === 'general' ? 'text-indigo-600' : 'text-gray-400'
                }`}
                aria-hidden="true"
              />
              <span className="truncate">General</span>
            </button>
            <button
              onClick={() => setActiveSection('mapDisplay')}
              className={`group flex w-full items-center rounded-md px-3 py-2 text-sm font-medium ${
                activeSection === 'mapDisplay'
                  ? 'bg-indigo-50 text-indigo-600'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <MapIcon
                className={`-ml-1 mr-3 h-6 w-6 flex-shrink-0 ${
                  activeSection === 'mapDisplay' ? 'text-indigo-600' : 'text-gray-400'
                }`}
                aria-hidden="true"
              />
              <span className="truncate">Map Display</span>
            </button>
            <button
              onClick={() => setActiveSection('colors')}
              className={`group flex w-full items-center rounded-md px-3 py-2 text-sm font-medium ${
                activeSection === 'colors'
                  ? 'bg-indigo-50 text-indigo-600'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <SwatchIcon
                className={`-ml-1 mr-3 h-6 w-6 flex-shrink-0 ${
                  activeSection === 'colors' ? 'text-indigo-600' : 'text-gray-400'
                }`}
                aria-hidden="true"
              />
              <span className="truncate">Colors</span>
            </button>
            <button
              onClick={() => setActiveSection('branding')}
              className={`group flex w-full items-center rounded-md px-3 py-2 text-sm font-medium ${
                activeSection === 'branding'
                  ? 'bg-indigo-50 text-indigo-600'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <PhotoIcon
                className={`-ml-1 mr-3 h-6 w-6 flex-shrink-0 ${
                  activeSection === 'branding' ? 'text-indigo-600' : 'text-gray-400'
                }`}
                aria-hidden="true"
              />
              <span className="truncate">Branding</span>
            </button>
            <button
              onClick={() => setActiveSection('email')}
              className={`group flex w-full items-center rounded-md px-3 py-2 text-sm font-medium ${
                activeSection === 'email'
                  ? 'bg-indigo-50 text-indigo-600'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <EnvelopeIcon
                className={`-ml-1 mr-3 h-6 w-6 flex-shrink-0 ${
                  activeSection === 'email' ? 'text-indigo-600' : 'text-gray-400'
                }`}
                aria-hidden="true"
              />
              <span className="truncate">Email</span>
            </button>
            <button
              onClick={() => setActiveSection('emailTemplates')}
              className={`group flex w-full items-center rounded-md px-3 py-2 text-sm font-medium ${
                activeSection === 'emailTemplates'
                  ? 'bg-indigo-50 text-indigo-600'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <DocumentTextIcon
                className={`-ml-1 mr-3 h-6 w-6 flex-shrink-0 ${
                  activeSection === 'emailTemplates' ? 'text-indigo-600' : 'text-gray-400'
                }`}
                aria-hidden="true"
              />
              <span className="truncate">Email Templates</span>
            </button>
            <button
              onClick={() => setActiveSection('payments')}
              className={`group flex w-full items-center rounded-md px-3 py-2 text-sm font-medium ${
                activeSection === 'payments'
                  ? 'bg-indigo-50 text-indigo-600'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <CreditCardIcon
                className={`-ml-1 mr-3 h-6 w-6 flex-shrink-0 ${
                  activeSection === 'payments' ? 'text-indigo-600' : 'text-gray-400'
                }`}
                aria-hidden="true"
              />
              <span className="truncate">Payments</span>
            </button>
            {!googleTagEnvOverride && (
              <button
                onClick={() => setActiveSection('analytics')}
                className={`group flex w-full items-center rounded-md px-3 py-2 text-sm font-medium ${
                  activeSection === 'analytics'
                    ? 'bg-indigo-50 text-indigo-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <ChartBarIcon
                  className={`-ml-1 mr-3 h-6 w-6 flex-shrink-0 ${
                    activeSection === 'analytics' ? 'text-indigo-600' : 'text-gray-400'
                  }`}
                  aria-hidden="true"
                />
                <span className="truncate">Analytics</span>
              </button>
            )}
            <button
              onClick={() => setActiveSection('customFields')}
              className={`group flex w-full items-center rounded-md px-3 py-2 text-sm font-medium ${
                activeSection === 'customFields'
                  ? 'bg-indigo-50 text-indigo-600'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <AdjustmentsHorizontalIcon
                className={`-ml-1 mr-3 h-6 w-6 flex-shrink-0 ${
                  activeSection === 'customFields' ? 'text-indigo-600' : 'text-gray-400'
                }`}
                aria-hidden="true"
              />
              <span className="truncate">Custom Fields</span>
            </button>
            {demoMode?.enabled && (
              <button
                onClick={() => setActiveSection('demo')}
                className={`group flex w-full items-center rounded-md px-3 py-2 text-sm font-medium ${
                  activeSection === 'demo'
                    ? 'bg-indigo-50 text-indigo-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <BeakerIcon
                  className={`-ml-1 mr-3 h-6 w-6 flex-shrink-0 ${
                    activeSection === 'demo' ? 'text-indigo-600' : 'text-gray-400'
                  }`}
                  aria-hidden="true"
                />
                <span className="truncate">Demo Mode</span>
              </button>
            )}
          </nav>
        </aside>

        <main className="mt-8 lg:col-span-9 lg:mt-0">
          {/* General Settings Section */}
          {activeSection === 'general' && (
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium leading-6 text-gray-900">General Settings</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Configure the name and branding of your site.
                </p>

                <form onSubmit={handleSubmit} className="mt-6 space-y-6">
                  <div>
                    <label htmlFor="siteName" className="block text-sm font-medium text-gray-700">
                      Site Name
                    </label>
                    <p className="mt-1 text-xs text-gray-500">
                      This name appears in the browser tab and next to the logo in the header.
                    </p>
                    <input
                      type="text"
                      name="siteName"
                      id="siteName"
                      value={formData.siteName}
                      onChange={(e) => setFormData({ ...formData, siteName: e.target.value })}
                      placeholder="Parish Recorder"
                      className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>

                  {error && (
                    <div className="rounded-md bg-red-50 p-4">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}

                  {success && (
                    <div className="rounded-md bg-green-50 p-4">
                      <p className="text-sm text-green-700">{success}</p>
                    </div>
                  )}

                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
                    >
                      {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Map Display Settings Section */}
          {activeSection === 'mapDisplay' && (
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium leading-6 text-gray-900">Map Display</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Configure how plots are displayed on the map.
                </p>

                <form onSubmit={handleSubmit} className="mt-6 space-y-6">
                  <div className="flex items-center justify-between py-2">
                    <div>
                      <span className="text-sm text-gray-900">Show Calculated Plot Size</span>
                      <p className="text-xs text-gray-500">Display estimated plot size in the map table and info popup</p>
                      <p className="text-xs text-amber-600 mt-1">Note: Size is calculated from GIS polygon dimensions and may not reflect actual surveyed dimensions.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, showPlotSize: !prev.showPlotSize }))}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                        formData.showPlotSize ? 'bg-indigo-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                          formData.showPlotSize ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  {error && (
                    <div className="rounded-md bg-red-50 p-4">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}

                  {success && (
                    <div className="rounded-md bg-green-50 p-4">
                      <p className="text-sm text-green-700">{success}</p>
                    </div>
                  )}

                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
                    >
                      {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Colors Settings Section */}
          {activeSection === 'colors' && (
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium leading-6 text-gray-900">Map Colors</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Configure the colors used to display plots on maps throughout the application, including the admin map and public portal.
                </p>

                <form onSubmit={handleColorSubmit} className="mt-6 space-y-6">
                  {[
                    { key: 'available', label: 'Available', desc: 'Plots with no contacts - vacant or unassigned' },
                    { key: 'reserved', label: 'Reserved', desc: 'Plots that are marked as reserved' },
                    { key: 'sold', label: 'Sold', desc: 'Plots with contacts but no interments yet' },
                    { key: 'interred', label: 'Occupied', desc: 'Plots with at least one interment' },
                    { key: 'hasVeteran', label: 'Veteran Occupied', desc: 'Plots with a veteran interred' },
                    { key: 'highlight', label: 'Highlight', desc: 'Temporary highlight for search results and navigation' }
                  ].map(item => (
                    <div key={item.key} className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <input
                          type="color"
                          id={item.key}
                          value={colorData[item.key] || DEFAULT_PLOT_COLORS[item.key]}
                          onChange={(e) => handleColorChange(item.key, e.target.value)}
                          className="h-10 w-10 rounded border border-gray-300 cursor-pointer"
                        />
                      </div>
                      <div className="flex-1">
                        <label htmlFor={item.key} className="block text-sm font-medium text-gray-700">
                          {item.label}
                        </label>
                        <p className="mt-1 text-xs text-gray-500">{item.desc}</p>
                        <input
                          type="text"
                          value={colorData[item.key] || DEFAULT_PLOT_COLORS[item.key]}
                          onChange={(e) => handleColorChange(item.key, e.target.value)}
                          className="mt-1 block w-32 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-xs font-mono"
                        />
                      </div>
                    </div>
                  ))}

                  {error && (
                    <div className="rounded-md bg-red-50 p-4">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}

                  {success && (
                    <div className="rounded-md bg-green-50 p-4">
                      <p className="text-sm text-green-700">{success}</p>
                    </div>
                  )}

                  <div className="flex justify-between">
                    <button
                      type="button"
                      onClick={resetColors}
                      className="inline-flex justify-center rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200"
                    >
                      Reset to Defaults
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
                    >
                      {loading ? 'Saving...' : 'Save Colors'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Branding Settings Section */}
          {activeSection === 'branding' && (
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium leading-6 text-gray-900">Logo Branding</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Customize the logos displayed throughout the application. Upload your own logos to personalize the experience for your organization.
                </p>

                <div className="mt-6 space-y-8">
                  {/* Header Logo */}
                  <div className="border-b border-gray-200 pb-8">
                    <h4 className="text-sm font-medium text-gray-900">Header Logo</h4>
                    <p className="mt-1 text-xs text-gray-500">
                      This logo appears in the navigation bar next to your site name. Recommended size: 40px height, SVG or PNG format.
                    </p>
                    <div className="mt-4 flex items-center gap-6">
                      <div className="flex-shrink-0 h-16 w-16 flex items-center justify-center bg-gray-100 rounded-lg">
                        <img
                          src={headerLogo}
                          alt="Current header logo"
                          className="max-h-12 max-w-12 object-contain"
                        />
                      </div>
                      <div className="flex-1">
                        <input
                          type="file"
                          ref={headerFileRef}
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/svg+xml,image/webp"
                          onChange={(e) => {
                            if (e.target.files?.[0]) {
                              handleLogoUpload('header', e.target.files[0]);
                              e.target.value = '';
                            }
                          }}
                          className="hidden"
                        />
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => headerFileRef.current?.click()}
                            disabled={uploadingHeader}
                            className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                          >
                            {uploadingHeader ? 'Uploading...' : 'Upload New Logo'}
                          </button>
                          {isCustomLogo(headerLogo) && (
                            <button
                              type="button"
                              onClick={() => handleResetLogo('header')}
                              disabled={loading}
                              className="text-sm text-red-600 hover:text-red-500"
                            >
                              Reset to Default
                            </button>
                          )}
                        </div>
                        {isCustomLogo(headerLogo) && (
                          <p className="mt-2 text-xs text-green-600">Using custom logo</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Login Logo */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Login Page Logo</h4>
                    <p className="mt-1 text-xs text-gray-500">
                      This logo appears on the login and registration page. Recommended size: 256px height, PNG format for best quality.
                    </p>
                    <div className="mt-4 flex items-start gap-6">
                      <div className="flex-shrink-0 h-32 w-32 flex items-center justify-center bg-gray-100 rounded-lg">
                        <img
                          src={loginLogo}
                          alt="Current login logo"
                          className="max-h-28 max-w-28 object-contain"
                        />
                      </div>
                      <div className="flex-1">
                        <input
                          type="file"
                          ref={loginFileRef}
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/svg+xml,image/webp"
                          onChange={(e) => {
                            if (e.target.files?.[0]) {
                              handleLogoUpload('login', e.target.files[0]);
                              e.target.value = '';
                            }
                          }}
                          className="hidden"
                        />
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => loginFileRef.current?.click()}
                            disabled={uploadingLogin}
                            className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                          >
                            {uploadingLogin ? 'Uploading...' : 'Upload New Logo'}
                          </button>
                          {isCustomLogo(loginLogo) && (
                            <button
                              type="button"
                              onClick={() => handleResetLogo('login')}
                              disabled={loading}
                              className="text-sm text-red-600 hover:text-red-500"
                            >
                              Reset to Default
                            </button>
                          )}
                        </div>
                        {isCustomLogo(loginLogo) && (
                          <p className="mt-2 text-xs text-green-600">Using custom logo</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {error && (
                    <div className="rounded-md bg-red-50 p-4">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}

                  {success && (
                    <div className="rounded-md bg-green-50 p-4">
                      <p className="text-sm text-green-700">{success}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Email Settings Section */}
          {activeSection === 'email' && (
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium leading-6 text-gray-900">Email Configuration</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Configure email sending for password resets, invoices, and notifications.
                </p>

                <div className="mt-6">
                  <EmailSettings />
                </div>
              </div>
            </div>
          )}

          {/* Email Templates Section */}
          {activeSection === 'emailTemplates' && (
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium leading-6 text-gray-900">Email Templates</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Customize email templates sent to users and contacts.
                </p>

                <div className="mt-6">
                  <EmailTemplates />
                </div>
              </div>
            </div>
          )}

          {/* Payments Section */}
          {activeSection === 'payments' && (
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <StripeSettings />
              </div>
            </div>
          )}

          {/* Analytics Section */}
          {activeSection === 'analytics' && !googleTagEnvOverride && (
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium leading-6 text-gray-900">Analytics</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Connect Google Analytics to track visitor activity on your site. Enter your Google Tag ID to enable tracking.
                </p>

                <form onSubmit={handleAnalyticsSubmit} className="mt-6 space-y-6">
                  <div>
                    <label htmlFor="googleTagId" className="block text-sm font-medium text-gray-700">
                      Google Tag ID
                    </label>
                    <p className="mt-1 text-xs text-gray-500">
                      Enter your Google Analytics measurement ID (e.g., G-XXXXXXXXXX). You can find this in your Google Analytics property settings under Data Streams.
                    </p>
                    <input
                      type="text"
                      name="googleTagId"
                      id="googleTagId"
                      value={analyticsData.googleTagId}
                      onChange={(e) => setAnalyticsData({ ...analyticsData, googleTagId: e.target.value })}
                      placeholder="G-XXXXXXXXXX"
                      className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm font-mono"
                    />
                  </div>

                  {analyticsData.googleTagId && !/^(G|GT|AW)-[A-Za-z0-9]+$/.test(analyticsData.googleTagId.trim()) && (
                    <div className="rounded-md bg-amber-50 p-4">
                      <p className="text-sm text-amber-700">
                        The tag ID format looks incorrect. It should start with G-, GT-, or AW- followed by alphanumeric characters (e.g., G-ABC123DEF4).
                      </p>
                    </div>
                  )}

                  <div className="rounded-md bg-blue-50 p-4">
                    <p className="text-sm text-blue-700">
                      <strong>How to get your Google Tag ID:</strong> Sign in to{' '}
                      <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="underline">Google Analytics</a>,
                      go to <strong>Admin</strong> &rarr; <strong>Data Streams</strong>, select your web stream (or create one),
                      and copy the <strong>Measurement ID</strong> that starts with "G-".
                    </p>
                  </div>

                  {error && (
                    <div className="rounded-md bg-red-50 p-4">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}

                  {success && (
                    <div className="rounded-md bg-green-50 p-4">
                      <p className="text-sm text-green-700">{success}</p>
                    </div>
                  )}

                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
                    >
                      {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {activeSection === 'customFields' && (
            <FlexFieldsConfig embedded={true} />
          )}

          {/* Demo Mode Section */}
          {activeSection === 'demo' && demoMode?.enabled && (
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium leading-6 text-gray-900">Demo Mode</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Demo mode is active. Data created after the cutoff date will be automatically reset at midnight each day.
                </p>

                <div className="mt-6 space-y-6">
                  {/* Demo Mode Status */}
                  <div className="rounded-md bg-amber-50 border border-amber-200 p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <BeakerIcon className="h-5 w-5 text-amber-400" aria-hidden="true" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-amber-800">Demo Mode Active</h3>
                        <div className="mt-2 text-sm text-amber-700">
                          <p><strong>Cutoff Date:</strong> {formatCutoffDate(demoMode.cutoffDate)}</p>
                          <p className="mt-1">All data created after this date will be automatically deleted at midnight.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Login Message Preview */}
                  {demoMode.message && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Login Page Message Preview</h4>
                      <p className="mt-1 text-xs text-gray-500">
                        This message is displayed on the login page when demo mode is enabled.
                      </p>
                      <div className="mt-2 rounded-md bg-gray-100 p-4">
                        <div
                          className="text-sm text-gray-700"
                          dangerouslySetInnerHTML={{ __html: demoMode.message }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Manual Reset Section */}
                  <div className="border-t border-gray-200 pt-6">
                    <h4 className="text-sm font-medium text-gray-900">Manual Reset</h4>
                    <p className="mt-1 text-sm text-gray-500">
                      Click the button below to immediately reset the demo data. This will delete all records created after the cutoff date.
                    </p>

                    <div className="mt-4">
                      <button
                        type="button"
                        onClick={handleDemoReset}
                        disabled={resettingDemo}
                        className="inline-flex items-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 disabled:opacity-50"
                      >
                        {resettingDemo ? 'Resetting...' : 'Reset Demo Data Now'}
                      </button>
                    </div>

                    {/* Reset Results */}
                    {resetResults && (
                      <div className="mt-4 rounded-md bg-gray-50 p-4">
                        <h5 className="text-sm font-medium text-gray-900">Records Deleted</h5>
                        <dl className="mt-2 grid grid-cols-2 gap-2 text-sm">
                          {Object.entries(resetResults).map(([table, count]) => (
                            <div key={table} className="flex justify-between">
                              <dt className="text-gray-500 capitalize">{table.replace(/([A-Z])/g, ' $1').trim()}:</dt>
                              <dd className="font-medium text-gray-900">{count}</dd>
                            </div>
                          ))}
                        </dl>
                      </div>
                    )}
                  </div>

                  {error && (
                    <div className="rounded-md bg-red-50 p-4">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}

                  {success && (
                    <div className="rounded-md bg-green-50 p-4">
                      <p className="text-sm text-green-700">{success}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default SiteSettings;
